import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClaimVoice() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: input, 2: recording, 3: success
  const [incidentType, setIncidentType] = useState('car_accident');
  const [title, setTitle] = useState('');
  const [textStatement, setTextStatement] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [createdClaimId, setCreatedClaimId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        chunks.push(e.data);
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioFile(blob);
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setAudioChunks(chunks);
      setIsRecording(true);
    } catch (err) {
      alert('Microphone access denied. Please type your statement instead.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create claim
      const claimData = {
        incident_type: incidentType,
        title: title,
        raw_text: textStatement || null,
        incident_time: new Date().toISOString()
      };

      const claimResponse = await axios.post(`${API}/claims`, claimData);
      const claimId = claimResponse.data.id;
      setCreatedClaimId(claimId);

      // If audio file exists, transcribe it
      if (audioFile) {
        const formData = new FormData();
        formData.append('audio', audioFile, 'recording.webm');

        await axios.post(`${API}/claims/${claimId}/transcribe`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      setStep(3);
    } catch (error) {
      console.error('Error creating claim:', error);
      alert('Failed to create claim. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-slate-900 to-slate-900">
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/')} className="text-white/60 hover:text-white transition-colors">
              ← Back
            </button>
            <div className="flex items-center space-x-3">
              <img src="/veritascore-logo.png" alt="VeritasCore" className="h-10 w-10" />
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight">VERITASCORE.IO</h1>
                <p className="text-xs text-blue-400">ClaimVoice — Capture the Story</p>
              </div>
            </div>
          </div>
          <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {step === 1 && (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
            <h2 className="text-3xl font-bold text-white mb-6">Capture What Happened</h2>
            <p className="text-gray-300 mb-2">Record or type your statement. Your story will be processed for VeritasCore verification.</p>
            <p className="text-xs text-teal-400 mb-8">Statement ready for VeritasCore analysis</p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Incident Type */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Incident Type</label>
                <select
                  value={incidentType}
                  onChange={(e) => setIncidentType(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="incident-type-select"
                >
                  <option value="car_accident">Car Accident</option>
                  <option value="slip_and_fall">Slip and Fall</option>
                  <option value="property_damage">Property Damage</option>
                  <option value="personal_injury">Personal Injury</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Brief Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Rear-end collision on Main St"
                  required
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="claim-title-input"
                />
              </div>

              {/* Recording or Text */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Your Statement</label>
                
                {/* Audio Recording */}
                <div className="mb-4">
                  {!isRecording && !audioFile && (
                    <button
                      type="button"
                      onClick={startRecording}
                      className="w-full bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 rounded-lg px-6 py-4 text-blue-300 font-semibold flex items-center justify-center space-x-3 transition-all"
                      data-testid="start-recording-btn"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
                      </svg>
                      <span>Record Audio Statement</span>
                    </button>
                  )}

                  {isRecording && (
                    <button
                      type="button"
                      onClick={stopRecording}
                      className="w-full bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 rounded-lg px-6 py-4 text-red-300 font-semibold flex items-center justify-center space-x-3 transition-all recording-pulse"
                      data-testid="stop-recording-btn"
                    >
                      <div className="w-4 h-4 bg-red-500 rounded-full"></div>
                      <span>Recording... Click to Stop</span>
                    </button>
                  )}

                  {audioFile && (
                    <div className="bg-green-500/20 border border-green-500/50 rounded-lg px-6 py-4 text-green-300 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                        </svg>
                        <span>Audio recorded successfully</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setAudioFile(null)}
                        className="text-red-300 hover:text-red-200"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                <div className="text-center text-gray-400 mb-4">OR</div>

                {/* Text Statement */}
                <textarea
                  value={textStatement}
                  onChange={(e) => setTextStatement(e.target.value)}
                  placeholder="Type your statement here... Describe what happened in detail."
                  rows={6}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  data-testid="claim-text-input"
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading || (!audioFile && !textStatement)}
                className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-4 rounded-lg transition-all"
                data-testid="create-claim-btn"
              >
                {loading ? 'Creating Claim...' : 'Create Claim'}
              </button>
            </form>
          </div>
        )}

        {step === 3 && (
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Claim Created Successfully!</h2>
            <p className="text-gray-300 mb-2">Your statement has been captured and structured.</p>
            <p className="text-xs text-teal-400 mb-8">Ready for VeritasCore verification</p>
            <div className="space-y-4">
              <button
                onClick={() => navigate(`/luluverify?claim_id=${createdClaimId}`)}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 rounded-lg transition-all"
                data-testid="add-evidence-btn"
              >
                Add Evidence (LuluVerify) →
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-white/10 hover:bg-white/20 text-white font-semibold py-4 rounded-lg transition-all"
                data-testid="view-claims-btn"
              >
                View All Claims
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}